import { Link } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { ChevronNextIcon, ClockIcon, JobsIcon, PinIcon, PlusIcon } from '@/components/icons';
import { JobRow, RegistrationPill } from '@/components/jobs/JobRow';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { Spinner } from '@/components/ui/Spinner';
import { WorkerCountTag } from '@/components/ui/WorkerCountTag';
import { useActiveJobs, type JobView } from '@/hooks/useActiveJobs';
import { usePendingRequestCount } from '@/hooks/usePendingRequestCount';
import { addDays, formatLongToday, greetingFor, todayInIsrael } from '@/lib/format';

export function HomePage() {
  const { me } = useAdmin();
  const jobs = useActiveJobs();
  const pending = usePendingRequestCount();
  const today = todayInIsrael();
  const weekEnd = addDays(today, 6);
  const firstName = me.displayName.split(' ')[0];

  return (
    <>
      <header className="mb-5 md:mb-6">
        <p className="text-[14px] text-ink-2">{formatLongToday()}</p>
        <h1 className="mt-0.5 text-[24px] font-bold leading-tight md:text-[30px]">
          {greetingFor()}, {firstName}
        </h1>
      </header>

      {jobs.state === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {jobs.state === 'error' && <Notice tone="error">{jobs.message}</Notice>}

      {jobs.state === 'ready' &&
        (() => {
          const todays = [...jobs.data.filter((j) => j.date === today)].sort((a, b) => a.reportTime.localeCompare(b.reportTime));
          const restOfWeek = jobs.data.filter((j) => j.date > today && j.date <= weekEnd);
          const todayWorkers = todays.reduce((sum, j) => sum + (j.totalWorkers || 0), 0);
          const weekWorkers = jobs.data
            .filter((j) => j.date >= today && j.date <= weekEnd)
            .reduce((sum, j) => sum + (j.totalWorkers || 0), 0);

          return (
            <>
              <section aria-labelledby="today-title" className="overflow-hidden rounded-card border border-line bg-surface">
                <div className="flex items-center justify-between gap-4 p-4 md:p-5">
                  <div>
                    <h2 id="today-title" className="text-[17px] font-bold">
                      מגיעים היום
                    </h2>
                    <p className="mt-0.5 text-[14px] text-ink-2">
                      {todays.length === 0 ? 'אין עבודות היום' : todays.length === 1 ? 'עבודה אחת' : `${todays.length} עבודות`}
                    </p>
                  </div>
                  <WorkerCountTag count={todayWorkers} size="lg" />
                </div>

                {todays.length > 0 && (
                  <ul className="border-t border-line">
                    {todays.map((job) => (
                      <TodayJobLine key={job.id} job={job} />
                    ))}
                  </ul>
                )}

                <dl className="grid grid-cols-3 border-t border-line bg-paper/60 text-center">
                  <Fact label="עבודות פעילות" value={jobs.data.length} />
                  <Fact label="עובדים השבוע" value={weekWorkers} divider />
                  <Fact label="בקשות ממתינות" value={pending} to="/requests" alert={pending > 0} divider />
                </dl>
              </section>

              <section className="mt-7">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-[18px] font-semibold">בהמשך השבוע</h2>
                  <Link to="/jobs" className="text-[14px] font-medium text-brand hover:underline">
                    כל העבודות
                  </Link>
                </div>
                {restOfWeek.length === 0 ? (
                  <EmptyState
                    icon={<JobsIcon size={30} />}
                    title="אין עבודות נוספות השבוע"
                    body="עבודות חדשות יופיעו כאן עם כמות העובדים שדווחה עד עכשיו."
                    action={
                      <Link
                        to="/jobs/new"
                        className="inline-flex min-h-11 items-center gap-2 rounded-control bg-brand px-4 text-[14px] font-semibold text-white"
                      >
                        <PlusIcon size={18} />
                        עבודה חדשה
                      </Link>
                    }
                  />
                ) : (
                  <ul className="space-y-3">
                    {restOfWeek.map((job) => (
                      <JobRow key={job.id} job={job} />
                    ))}
                  </ul>
                )}
              </section>
            </>
          );
        })()}
    </>
  );
}

/** שורה קומפקטית של עבודה של היום, בתוך כרטיס הסיכום. */
function TodayJobLine({ job }: { job: JobView }) {
  return (
    <li className="border-b border-line last:border-b-0">
      <Link to={`/jobs/${job.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-sunken md:px-5 md:hover:bg-sunken/60">
        <div className="tabular flex w-14 shrink-0 flex-col items-center rounded-[8px] bg-sunken py-1.5">
          <ClockIcon size={14} className="text-ink-3" />
          <span className="text-[15px] font-bold">{job.reportTime}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">{job.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-[13px] text-ink-2">
            <PinIcon size={14} className="shrink-0" />
            <span className="truncate">{job.location}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <RegistrationPill state={job.registration} />
            {job.pendingRequestCount > 0 && (
              <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[12px] font-semibold text-danger">
                {job.pendingRequestCount} בקשות
              </span>
            )}
          </div>
        </div>
        <div className="tabular shrink-0 text-center">
          <div className="text-[20px] font-extrabold leading-none">{job.totalWorkers}</div>
          <div className="mt-0.5 text-[11px] text-ink-3">עובדים</div>
        </div>
        <ChevronNextIcon size={18} className="shrink-0 text-ink-3" />
      </Link>
    </li>
  );
}

function Fact({
  label,
  value,
  to,
  alert,
  divider,
}: {
  label: string;
  value: number;
  to?: string;
  alert?: boolean;
  divider?: boolean;
}) {
  const content = (
    <>
      {/* dt לפני dd לסמנטיקה תקינה; flex-col-reverse מציג את המספר למעלה */}
      <dt className="mt-0.5 text-[12px] text-ink-3">{label}</dt>
      <dd className={`tabular text-[22px] font-bold ${alert ? 'text-danger' : ''}`}>{value}</dd>
    </>
  );
  return (
    <div className={`flex flex-col-reverse py-3 ${divider ? 'border-s border-line' : ''}`}>
      {to ? (
        <Link to={to} className="flex flex-col-reverse">
          {content}
        </Link>
      ) : (
        content
      )}
    </div>
  );
}
